import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { createUser, getUser, updateUser } from '../../services/userService';
import { getSystems } from '../../services/systemService';
import { getAllPublicEntitiesSimple } from '../../services/publicEntityService';
import { SystemResponse } from '../../types/system';
import { PublicEntitySimpleResponse } from '../../types/publicEntity';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import Select from 'react-select';

import { CreateUserRequest, UpdateUserRequest, UserResponse } from '../../types/user';

const UserForm: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const isEditMode = Boolean(userId);
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    phone_number: '',
    password: '',
    full_name: '',
    cpf: '',
    is_active: true,
    is_system_admin: false,
    systems: [],
    entities: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [systems, setSystems] = useState<SystemResponse[]>([]);
  const [publicEntities, setPublicEntities] = useState<PublicEntitySimpleResponse[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  /**
   * Função para buscar sistemas disponíveis.
   */
  const fetchSystems = async () => {
    try {
      const data = await getSystems();
      setSystems(data);
    } catch (error) {
      console.error('Erro ao buscar sistemas:', error);
      swal('Erro', 'Não foi possível buscar os sistemas.', 'error');
    }
  };

  /**
   * Função para buscar entidades públicas disponíveis.
   */
  const fetchPublicEntities = async () => {
    try {
      const data = await getAllPublicEntitiesSimple();
      setPublicEntities(data);
    } catch (error) {
      console.error('Erro ao buscar entidades públicas:', error);
      swal('Erro', 'Não foi possível buscar as entidades públicas.', 'error');
    }
  };  

  /**
   * Função para buscar os dados do usuário em modo de edição.
   */
  const fetchUserData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data: UserResponse = await getUser(userId);
      setFormData({
        email: data.email,
        phone_number: data.phone_number,
        password: '', // Não preencher a senha
        full_name: data.full_name,
        cpf: data.cpf || '',
        is_active: data.is_active,
        is_system_admin: data.is_system_admin,
        systems: data.systems.map((system) => system.id),
        entities: data.entities.map((entity) => entity.id),
      });
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      swal('Erro', 'Não foi possível buscar os dados do usuário.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
    fetchPublicEntities();
    if (isEditMode && userId) {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, userId]);

  /**
   * Função para lidar com mudanças nos campos do formulário.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'is_active' || name === 'is_system_admin' ? checked : value,
    }));
  };

  /**
   * Função para lidar com a seleção de sistemas.
   */
  const handleSystemsChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData((prev) => ({
      ...prev,
      systems: selectedIds,
    }));
  };

  /**
   * Função para lidar com a seleção de entidades públicas.
   */
  const handleEntitiesChange = (selectedOptions: any) => {
    const selectedIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData((prev) => ({
      ...prev,
      entities: selectedIds,
    }));
  };

  /**
   * Função para lidar com o envio do formulário.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação básica
    if (!formData.email || !formData.full_name || !formData.phone_number) {
      swal('Erro', 'Email, Nome Completo e Telefone são obrigatórios.', 'error');
      return;
    }

    // Em modo de criação, a senha é obrigatória
    if (!isEditMode && !formData.password) {
      swal('Erro', 'A senha é obrigatória para criação de usuário.', 'error');
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && userId) {
        // Atualizar usuário
        const updateData: UpdateUserRequest = {
          email: formData.email,
          full_name: formData.full_name,
          phone_number: formData.phone_number,
          cpf: formData.cpf || undefined,
          is_active: formData.is_active,
          is_system_admin: formData.is_system_admin,
          systems: formData.systems,
          entities: formData.entities,
        };

        // Se a senha foi preenchida, incluí-la
        if (formData.password) {
          updateData.password = formData.password;
        }

        await updateUser(userId, updateData);
        swal('Sucesso', 'Usuário atualizado com sucesso.', 'success');
      } else {
        // Criar novo usuário
        const createData: CreateUserRequest = {
          phone_number: formData.phone_number,
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          cpf: formData.cpf || undefined,
          is_active: formData.is_active,
          is_system_admin: formData.is_system_admin,
          systems: formData.systems,
          entities: formData.entities,
        };

        await createUser(createData);
        swal('Sucesso', 'Usuário criado com sucesso.', 'success');
      }
      navigate('/usuarios');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      const message = isEditMode ? 'Não foi possível atualizar o usuário.' : 'Não foi possível criar o usuário.';
      swal('Erro', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se o usuário tem permissão
  if (!user?.is_system_admin) {
    return (
      <Typography variant="h6" color="error">
        Você não tem permissão para acessar esta página.
      </Typography>
    );
  }

  // Opções para seleção múltipla de sistemas
  const systemsOptions = systems.map((system) => ({
    value: system.id,
    label: system.name,
  }));

  // Opções para seleção múltipla de entidades públicas
  const entitiesOptions = publicEntities.map((entity) => ({
    value: entity.id,
    label: entity.name,
  }));

  // Valores selecionados para sistemas
  const selectedSystems = systemsOptions.filter((option) => formData.systems.includes(option.value));

  // Valores selecionados para entidades públicas
  const selectedEntities = entitiesOptions.filter((option) => formData.entities.includes(option.value));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Editar Usuário' : 'Adicionar Usuário'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Nome Completo */}
            <Grid item xs={12}>
              <TextField
                label="Nome Completo"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            {/* Telefone */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefone"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            {/* CPF */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="CPF"
                name="cpf"
                value={formData.cpf}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            {/* Senha */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Senha"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                fullWidth
                required={!isEditMode}
                helperText={isEditMode ? 'Deixe em branco para não alterar a senha.' : ''}
              />
            </Grid>

            {/* Ativo */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active}
                    onChange={handleChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Ativo"
              />
            </Grid>

            {/* Administrador do Sistema */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_system_admin}
                    onChange={handleChange}
                    name="is_system_admin"
                    color="primary"
                  />
                }
                label="Administrador do Sistema"
              />
            </Grid>

            {/* Seleção de Sistemas */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Sistemas Associados
              </Typography>
              <Select
                isMulti
                options={systemsOptions}
                value={selectedSystems}
                onChange={handleSystemsChange}
                placeholder="Selecione os sistemas..."
              />
            </Grid>

            {/* Seleção de Entidades Públicas */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Entidades Públicas Associadas
              </Typography>
              <Select
                isMulti
                options={entitiesOptions}
                value={selectedEntities}
                onChange={handleEntitiesChange}
                placeholder="Selecione as entidades públicas..."
              />
            </Grid>
          </Grid>

          {/* Botões de ação */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default UserForm;